import React from 'react'
import { Chip, List, ListItem, ListItemText, ListSubheader, TextField, Typography } from '@material-ui/core'
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles'

import palette from '../../../../themes/palette'
import type { Mailer, MailerGroup } from '../../../../api/EmailTestBenchApi'
import { FILTER_LABEL, NO_MATCHES } from './copy'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    filter: { padding: theme.spacing(1.5, 2) },
    // The catalogue is 48 rows; the detail pane beside it is short. Without a
    // max height the page scrolls as one column and the actions leave the
    // viewport as soon as anything is selected.
    list: { maxHeight: 560, overflowY: 'auto', paddingTop: 0 },
    // The space and the seam between sections live HERE, on the group <ul>, and
    // deliberately not on the subheader below it. That subheader is sticky, and
    // a sticky box is offset by its MARGIN edge -- a marginTop there would hold
    // the stuck header that far down the pane and let rows scroll up through
    // the transparent strip above it.
    group: { padding: 0 },
    groupSeam: {
      marginTop: theme.spacing(1.5),
      // Same quiet hairline the admin sidebar uses for its own group seam:
      // lightestgrey, so it reads as a grouping rather than as a rule.
      borderTop: `1px solid ${palette.lightestgrey}`
    },
    // A category label, not a heavier row. MUI's default subheader is 0.875rem
    // at weight 500 in textSecondary -- the same size as the body2 mailer
    // labels beneath it and *lighter* than them -- so the group name read as
    // part of its own list rather than as the head of it. Smaller, uppercase,
    // letter-spaced and darker makes it a different kind of text, which is what
    // a header has to be. Matches the small-caps heading idiom in ContextPanel.
    subheader: {
      // Load-bearing, not cosmetic: this header sticks to the top of a 560px
      // scrolling list, and the opaque background is the only thing stopping
      // rows showing through it. The clear space above the text is padding
      // rather than margin for the same reason -- padding stays painted.
      backgroundColor: palette.white,
      color: palette.darkergrey,
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
      fontSize: 11,
      fontWeight: 700,
      lineHeight: '20px',
      paddingTop: theme.spacing(1.5),
      paddingBottom: theme.spacing(0.5)
    },
    row: { alignItems: 'flex-start' },
    // The registry key, for whoever is confirming which mailer this is. Ashley
    // ignores it; Aki needs it. Small and monospace so it reads as a subtitle
    // rather than as the name of the thing.
    key: { fontFamily: 'monospace', fontSize: 11, color: palette.lightergrey },
    chip: { marginLeft: theme.spacing(1), marginTop: theme.spacing(0.5) },
    // The chips stack rather than sitting side by side. A row can carry two --
    // its audience plus `dormant` or `locked` -- and on a pane this narrow a
    // second chip on the same line eats the label instead of the gutter.
    badges: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end' },
    // Distinct from both of the other two chip states, because it means a third
    // thing: the audience chip is filled, `locked` is a plain grey outline, and
    // this is an amber outline. Amber rather than red on purpose -- nothing is
    // broken here, and a dormant row is still worth clicking.
    dormantChip: {
      color: palette.orange900,
      borderColor: palette.lightOrange
    },
    // `locked` no longer has a greyed-out row behind it to lean on -- the rows
    // became selectable so their detail pane could explain them -- so this chip
    // now carries "you cannot fire this one" by itself. Muted rather than
    // coloured: it is the absence of an action, not a warning.
    lockedChip: {
      color: palette.grey,
      borderColor: palette.lightestgrey
    },
    empty: { padding: theme.spacing(2) }
  })
)

interface Props {
  groups: MailerGroup[]
  availableCount: number
  filter: string
  onFilterChange(value: string): void
  selectedKey?: string
  onSelect(mailer: Mailer): void
}

/** Case-insensitive, over both the label and the key, so either works. */
export const matchesFilter = (mailer: Mailer, filter: string): boolean => {
  const needle = filter.trim().toLowerCase()
  if (!needle) return true
  return mailer.label.toLowerCase().includes(needle) || mailer.key.toLowerCase().includes(needle)
}

export const filterGroups = (groups: MailerGroup[], filter: string): MailerGroup[] =>
  groups
    .map((group) => ({ ...group, mailers: group.mailers.filter((m) => matchesFilter(m, filter)) }))
    .filter((group) => group.mailers.length > 0)

/**
 * The left pane: 48 mailers in their sections, each row showing the human label,
 * the registry key beneath it, and who the email goes to.
 *
 * The audience chip is load-bearing rather than decorative. In the
 * specific_conversation_invitation_* family, `request` goes to the HOST and its
 * three siblings go to the requester -- same parameters, opposite recipients --
 * so the chip is the only thing on screen that distinguishes them.
 */
const MailerCatalogue: React.FC<Props> = ({
  groups,
  availableCount,
  filter,
  onFilterChange,
  selectedKey,
  onSelect
}) => {
  const classes = useStyles()
  const visible = filterGroups(groups, filter)

  return (
    <>
      <div className={classes.filter}>
        <TextField
          fullWidth
          variant='outlined'
          size='small'
          type='search'
          id='ic-bench-filter'
          label={FILTER_LABEL(availableCount)}
          value={filter}
          onChange={(e) => onFilterChange(e.target.value)}
        />
      </div>

      {visible.length === 0 ? (
        <Typography variant='body2' color='textSecondary' className={classes.empty}>
          {NO_MATCHES}
        </Typography>
      ) : (
        <List dense className={classes.list} subheader={<li />}>
          {visible.map((group, index) => (
            <li key={group.name}>
              <ul className={`${classes.group} ${index > 0 ? classes.groupSeam : ''}`}>
                <ListSubheader className={classes.subheader} disableSticky={false}>
                  {group.name} ({group.mailers.length})
                </ListSubheader>
                {group.mailers.map((mailer) => (
                  <ListItem
                    button
                    key={mailer.key}
                    className={classes.row}
                    id={`ic-bench-mailer-${mailer.key}`}
                    // 🔴 EVERY ROW IS SELECTABLE, INCLUDING THE LOCKED THREE.
                    // `locked` means one thing only: the actions on the detail
                    // pane do not arm. It never meant "you may not look at
                    // this" -- and a row that could not be opened could not
                    // explain itself either, which left three of the platform's
                    // emails as a greyed line nobody could interrogate.
                    //
                    // This needs no new guard. Both `Show me` and `Send it to
                    // me` in EmailTestBench.tsx already gate on
                    // `!selected.runnable`, so a locked row opens, shows its
                    // context and its reason, and arms neither button.
                    selected={mailer.key === selectedKey}
                    onClick={() => onSelect(mailer)}
                  >
                    {/* Label, key, chips -- and no prose. The explanations that
                        used to hang under the slug moved to the detail pane's
                        "About this email" section: this is a 48-row list, and a
                        list you cannot scan is not doing the one job it has.

                        🔴 THE SAFETY PROPERTY THAT USED TO LIVE HERE, AND WHERE
                        IT WENT. The dormancy sentence was kept in the row
                        because a send landing nowhere has to read as EXPECTED
                        BEFORE it happens, not be explained afterwards. That
                        still holds, and it is still enforced -- by the shape of
                        the page rather than by this sentence. You cannot reach
                        a send button without first selecting a row, and
                        selecting a row renders the About section above the
                        buttons. The pre-click signal is the amber `dormant`
                        chip below, which is unchanged. What was removed is the
                        paragraph, not the warning. */}
                    <ListItemText
                      disableTypography
                      primary={<Typography variant='body2'>{mailer.label}</Typography>}
                      secondary={<span className={classes.key}>{mailer.key}</span>}
                    />
                    {/* Two chips where a row needs two, never one standing in
                        for the other. This is the ONLY place recipient_role
                        appears anywhere on the page -- the detail pane does not
                        repeat it -- so `dormant` or `locked` replacing the
                        audience would trade a fact away rather than add one.
                        Locked rows used to do exactly that, which made the
                        three of them the only rows on the page with no audience
                        shown. */}
                    <div className={classes.badges}>
                      <Chip
                        size='small'
                        className={classes.chip}
                        variant={mailer.runnable ? 'default' : 'outlined'}
                        label={mailer.recipient_role}
                      />
                      {mailer.dormant && (
                        <Chip
                          size='small'
                          className={`${classes.chip} ${classes.dormantChip}`}
                          variant='outlined'
                          label='dormant'
                        />
                      )}
                      {!mailer.runnable && (
                        <Chip
                          size='small'
                          className={`${classes.chip} ${classes.lockedChip}`}
                          variant='outlined'
                          label='locked'
                        />
                      )}
                    </div>
                  </ListItem>
                ))}
              </ul>
            </li>
          ))}
        </List>
      )}
    </>
  )
}

export default MailerCatalogue
